import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

function VendorShow() {
    const { id } = useParams();
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadVendor();
    }, [id]);

    const loadVendor = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/vendors/${id}`);
            if (response.success) {
                setVendor(response.data);
            } else {
                setError(response.message || 'Failed to load vendor');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Loading vendor...</div>;
    if (error) return <div className="mt-16 p-8 text-center text-red-500 font-medium">{error}</div>;
    if (!vendor) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Vendor not found</div>;

    return (
        <div className="mt-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <Link to="/vendors" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-800">{vendor.name}</h1>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${vendor.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                            {vendor.active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <p className="text-slate-500 mt-1 ml-11">{vendor.city ? `${vendor.city}, ${vendor.state || ''}` : 'Location not set'}</p>
                </div>
                <div className="flex items-center gap-3 ml-11 sm:ml-0">
                    <Link to={`/vendors/${vendor.id}/edit`} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors shadow-sm text-sm">
                        Edit
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Details */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-slate-800 mb-4">Vendor Details</h2>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                            <div>
                                <dt className="text-sm text-slate-500">Phone</dt>
                                <dd className="font-medium text-slate-800">{vendor.phone || '-'}</dd>
                            </div>
                            <div>
                                <dt className="text-sm text-slate-500">Email</dt>
                                <dd className="font-medium text-slate-800">{vendor.email || '-'}</dd>
                            </div>
                            <div>
                                <dt className="text-sm text-slate-500">GSTIN</dt>
                                <dd className="font-medium text-slate-800">{vendor.gstin || '-'}</dd>
                            </div>
                            <div>
                                <dt className="text-sm text-slate-500">Created At</dt>
                                <dd className="font-medium text-slate-800">{new Date(vendor.created_at).toLocaleDateString()}</dd>
                            </div>
                        </dl>
                        {vendor.address && (
                            <div className="mt-4 pt-4 border-t border-slate-100">
                                <dt className="text-sm text-slate-500 mb-1">Address</dt>
                                <dd className="text-slate-700 whitespace-pre-wrap">{vendor.address}</dd>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[300px]">
                        <div className="px-6 py-4 border-b border-slate-200">
                            <h2 className="text-lg font-bold text-slate-800">Recent Purchases</h2>
                        </div>
                        <div className="p-8 text-center text-slate-500 font-medium">
                            Purchase history coming soon
                        </div>
                    </div>
                </div>

                {/* Right Column: Balance Card */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-indigo-600 rounded-2xl shadow-lg p-6 text-white text-center">
                        <h2 className="text-lg font-semibold opacity-80 uppercase tracking-wider mb-2">Current Balance</h2>
                        <p className="text-4xl font-bold">₹{vendor.balance?.toLocaleString()}</p>
                        <p className="mt-2 text-sm opacity-80 uppercase tracking-widest">Outstanding</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VendorShow;
